/**
 * Analytics Service
 * Aggregates analytics data from all RCM Platform services
 */

import axios from 'axios';
import { getServiceApiUrl } from '../utils/constants';
import { attachAxiosAccessRefresh } from '@saas-foundation/shared-frontend/utils/sessionKeepAlive';

// Base API client
/** Per-request cap so missing RCM backends cannot leave App Analytics spinning forever (axios default is no timeout). */
const ANALYTICS_HTTP_TIMEOUT_MS = 15000;

const createApiClient = (baseURL) => {
  const client = axios.create({
    baseURL,
    timeout: ANALYTICS_HTTP_TIMEOUT_MS,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  attachAxiosAccessRefresh(client);

  // Request interceptor
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Add organization_id to all requests if available
      const organizationId = localStorage.getItem('selectedOrganizationId') || 
                             localStorage.getItem('organizationId');
      if (organizationId) {
        // Merge with existing params
        config.params = {
          ...config.params,
          organization_id: organizationId
        };
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  client.interceptors.response.use(
    (response) => response.data,
    (error) => {
      // Handle network/CORS errors (no response object)
      if (!error.response) {
        if (error.code === 'ECONNABORTED') {
          console.warn(`Analytics API timeout (${ANALYTICS_HTTP_TIMEOUT_MS}ms): ${error.config?.url}`);
        } else {
          console.warn(`Analytics API Network/CORS Error: ${error.config?.url} - returning null`);
        }
        return null;
      }
      // Handle 404 errors gracefully - endpoint may not exist yet
      if (error.response?.status === 404) {
        console.warn(`Analytics endpoint not found: ${error.config?.url} - returning null`);
        return null;
      }
      // Log other errors but don't break the analytics page
      if (error.response?.status >= 500) {
        console.error(`Analytics API Server Error: ${error.response?.status} - ${error.config?.url}`);
        return null;
      }
      // Handle CORS errors (usually 0 status or no response)
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        console.warn(`Analytics API Network/CORS Error: ${error.config?.url} - returning null`);
        return null;
      }
      console.error('Analytics API Error:', error);
      return null; // Return null instead of rejecting to prevent breaking the page
    }
  );

  return client;
};

// Service-specific clients
const denialService = createApiClient(getServiceApiUrl('DENIAL_MANAGEMENT'));
const claimsService = createApiClient(getServiceApiUrl('CLAIMS_PROCESSING'));
const chargeCaptureBase = getServiceApiUrl('CHARGE_CAPTURE');
const chargeService = chargeCaptureBase ? createApiClient(chargeCaptureBase) : null;
const insuranceService = createApiClient(getServiceApiUrl('INSURANCE_VERIFICATION'));
const priorAuthService = createApiClient(getServiceApiUrl('PRIOR_AUTHORIZATION'));

const defaultSummaryMetrics = () => ({
  denials: {
    totalDenials: 0,
    denialRate: 0,
    totalRecovered: 0,
    pendingAppeals: 0,
    aiAccuracy: 0,
  },
  claims: {
    totalProcessed: 0,
    successRate: 0,
    totalCharged: 0,
    totalPaid: 0,
    automationRate: 0,
  },
  charges: {
    totalCharges: 0,
    totalAmount: 0,
    avgChargeAmount: 0,
  },
});

export const analyticsService = {
  // Get all analytics data from all services
  async getAllAnalytics(timeRange = '30d') {
    try {
      const [
        denialStats,
        denialTrends,
        denialAIInsights,
        claimsDashboard,
        claimsAnalytics,
        claimsAIInsights,
        chargeMetrics,
        // Add other services as they become available
      ] = await Promise.allSettled([
        // Denial Management - dashboard routes are at /api/v1/dashboard (router prefix is /api/v1, dashboard prefix is /dashboard)
        denialService.get(`/api/v1/dashboard/stats`, { params: { time_range: timeRange } }).catch(() => null),
        denialService.get(`/api/v1/dashboard/trends`, { params: { time_range: timeRange } }).catch(() => null),
        denialService.get(`/api/v1/dashboard/ai-insights`, { params: { time_range: timeRange } }).catch(() => null),
        
        // Claims Processing - reports routes are at /api/reports (no /v1)
        claimsService.get('/api/reports/dashboard').catch(() => null),
        claimsService.get('/api/reports/processing-analytics').catch(() => null),
        claimsService.get('/api/reports/ai-insights').catch(() => null),
        
        // Charge Management - reports routes are at /reports (no /api/v1); optional if service not deployed
        chargeService
          ? chargeService
              .get('/reports/charge-metrics', {
                params: {
                  date_from: analyticsService.getDateFrom(timeRange),
                  date_to: new Date().toISOString(),
                },
              })
              .catch(() => null)
          : Promise.resolve(null),
      ]);

      return {
        denials: {
          stats: denialStats.status === 'fulfilled' ? denialStats.value : null,
          trends: denialTrends.status === 'fulfilled' ? denialTrends.value : null,
          aiInsights: denialAIInsights.status === 'fulfilled' ? denialAIInsights.value : null,
        },
        claims: {
          dashboard: claimsDashboard.status === 'fulfilled' ? claimsDashboard.value : null,
          analytics: claimsAnalytics.status === 'fulfilled' ? claimsAnalytics.value : null,
          aiInsights: claimsAIInsights.status === 'fulfilled' ? claimsAIInsights.value : null,
        },
        charges: {
          metrics: chargeMetrics.status === 'fulfilled' ? chargeMetrics.value : null,
        },
        insurance: null, // To be implemented
        priorAuth: null, // To be implemented
      };
    } catch (error) {
      console.error('Error fetching all analytics:', error);
      return {
        denials: null,
        claims: null,
        charges: null,
        insurance: null,
        priorAuth: null,
      };
    }
  },

  /** Derive dashboard cards from one getAllAnalytics payload (avoids duplicate fan-out HTTP). */
  buildSummaryFromAllAnalytics(allAnalytics) {
    try {
      if (!allAnalytics) return defaultSummaryMetrics();
      return {
        denials: {
          totalDenials: allAnalytics?.denials?.stats?.denial_summary?.total_denials || 0,
          denialRate: allAnalytics?.denials?.stats?.denial_summary?.denial_rate || 0,
          totalRecovered: allAnalytics?.denials?.stats?.financial_summary?.total_recovered || 0,
          pendingAppeals: allAnalytics?.denials?.stats?.workflow_summary?.pending_appeals || 0,
          aiAccuracy: allAnalytics?.denials?.stats?.ai_performance?.accuracy || 0,
        },
        claims: {
          totalProcessed: allAnalytics?.claims?.dashboard?.processing_summary?.total_processed || 0,
          successRate: allAnalytics?.claims?.dashboard?.processing_summary?.success_rate || 0,
          totalCharged: allAnalytics?.claims?.dashboard?.financial_summary?.total_charged || 0,
          totalPaid: allAnalytics?.claims?.dashboard?.financial_summary?.total_paid || 0,
          automationRate: allAnalytics?.claims?.dashboard?.processing_summary?.automated_processing || 0,
        },
        charges: {
          totalCharges: allAnalytics?.charges?.metrics?.total_charges || 0,
          totalAmount: allAnalytics?.charges?.metrics?.total_amount || 0,
          avgChargeAmount: allAnalytics?.charges?.metrics?.avg_charge_amount || 0,
        },
      };
    } catch (error) {
      console.error('Error building summary metrics:', error);
      return defaultSummaryMetrics();
    }
  },

  // Get summary metrics for quick display (single round-trip to backends)
  async getSummaryMetrics(timeRange = '30d') {
    try {
      const allAnalytics = await this.getAllAnalytics(timeRange);
      return this.buildSummaryFromAllAnalytics(allAnalytics);
    } catch (error) {
      console.error('Error fetching summary metrics:', error);
      return defaultSummaryMetrics();
    }
  },

  // Helper to calculate date from time range
  getDateFrom(timeRange) {
    const now = new Date();
    const days = {
      '7d': 7,
      '30d': 30,
      '90d': 90,
      '1y': 365,
    };
    const daysBack = days[timeRange] || 30;
    const date = new Date(now);
    date.setDate(date.getDate() - daysBack);
    return date.toISOString();
  },
};

// Export helper function for use in API calls
analyticsService.getDateFrom = (timeRange) => {
  const now = new Date();
  const days = {
    '7d': 7,
    '30d': 30,
    '90d': 90,
    '1y': 365,
  };
  const daysBack = days[timeRange] || 30;
  const date = new Date(now);
  date.setDate(date.getDate() - daysBack);
  return date.toISOString();
};

